/* eslint-disable no-alert */
import React, { useEffect, useState } from 'react'
import { EditOutlined } from '@ant-design/icons'
import { Button, Input, Radio, Tooltip } from 'antd'
import { useTranslation } from 'lib/i18n'

import useVideoRequestDescriptionSuggestions from 'hooks/useVideoRequestDescriptionSuggestions'

import styles from './TextAreaWithSuggestions.module.css'

const { TextArea } = Input

export default function TextAreaWithSuggestions({
  profileId,
  onChange,
  value,
  videoRequestDescriptionSuggestionId = '',
  textareaStyle = {},
  id,
  onChangeSuggestion,
  requestOccasionId = '',
  profileProduct,
  placeholder = '',
  hideSuggestions = false,
}) {
  const productId = profileProduct?.product?.id
  const { t } = useTranslation()
  const { videoRequestDescriptionSuggestions = [] } =
    useVideoRequestDescriptionSuggestions({ profileId, productId })
  const [tooltipVisible, setTooltipVisible] = useState(false)

  const suggestions = videoRequestDescriptionSuggestions.filter((s) => {
    if (!s.requestOccasionId) return true
    return s.requestOccasionId === requestOccasionId
  })

  useEffect(() => {
    // Select the first suggestion after they are loaded from api, unless a instruction is already present
    if (!value) {
      if (
        Array.isArray(suggestions) &&
        suggestions.length > 0 &&
        requestOccasionId
      ) {
        const suggestion = suggestions[0]
        onChangeSuggestion({
          videoRequestDescriptionSuggestionId: suggestion.id,
          instructions: suggestion.description,
        })
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videoRequestDescriptionSuggestions])

  return (
    <>
      {!hideSuggestions && suggestions.length > 0 && (
        <div style={{ paddingBottom: 10, marginTop: 0 }}>
          <Radio.Group
            onChange={(e) => {
              const suggestionId = e.target.value
              const suggestion = suggestions.find((s) => s.id === suggestionId)

              const hasWrittenCustomText = Boolean(
                !videoRequestDescriptionSuggestionId &&
                  value &&
                  value.length > 5, // dont show warning if only a few characters
              )
              const isSameAsAnySuggestion =
                videoRequestDescriptionSuggestions.some(
                  (s) => s.description === value,
                )

              // If the customer has written their own text, ask before replacing it
              if (
                !hasWrittenCustomText ||
                isSameAsAnySuggestion ||
                window.confirm(
                  t(
                    'TextAreaWithSuggestions.confirmReplaceText',
                    'This will remove the text you have written , do you want to continue',
                  ),
                )
              ) {
                onChangeSuggestion({
                  videoRequestDescriptionSuggestionId: suggestionId,
                  instructions: suggestion ? suggestion.description : '',
                })
              }
            }}
            value={videoRequestDescriptionSuggestionId}
          >
            {suggestions.map((suggestion) => (
              <Radio key={suggestion.id} value={suggestion.id}>
                {suggestion.title}
              </Radio>
            ))}
            <Radio key="empty" value="">
              {t('TextAreaWithSuggestions.writeYourOwn', 'Write your own')}
            </Radio>
          </Radio.Group>
        </div>
      )}

      <Tooltip
        title={t(
          'TextAreaWithSuggestions.editTooltip',
          'Edit this text to make it your own',
        )}
        visible={tooltipVisible && videoRequestDescriptionSuggestionId}
      >
        {!videoRequestDescriptionSuggestionId ||
        videoRequestDescriptionSuggestionId === '' ? (
          <TextArea
            id={id}
            onChange={onChange}
            value={value}
            style={textareaStyle}
            placeholder={placeholder}
          />
        ) : (
          <div
            className={styles.textSuggestion}
            role="button"
            onClick={() => {
              setTooltipVisible(false)
              onChangeSuggestion({
                videoRequestDescriptionSuggestionId: '',
                instructions: value,
              })
            }}
            tabIndex={0}
          >
            {value}

            <Button
              className={styles.editButton}
              type="link"
              size="small"
              icon={<EditOutlined />}
              onMouseEnter={() => setTooltipVisible(true)}
              onMouseLeave={() => setTooltipVisible(false)}
            >
              {t('TextAreaWithSuggestions.edit', 'Edit')}
            </Button>
          </div>
        )}
      </Tooltip>
    </>
  )
}
